import ApprovalIcon from '@mui/icons-material/Approval'
import GradingIcon from '@mui/icons-material/Grading'
import PlaylistPlayIcon from '@mui/icons-material/PlaylistPlay'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'

import { formatDate } from '@util/date-util'

import type { AuthenticatedUser } from '../../../auth/types/AuthenticatedUser'
import { ConfirmButton } from '../../../theme/components/ConfirmButton'
import { PendingIcon } from '../../../theme/components/PendingIcon'
import { PropertyRow } from '../../../theme/components/PropertyRow'
import type { Step } from '../../../theme/components/Stepper'
import { Stepper } from '../../../theme/components/Stepper'
import { StyledError } from '../../../theme/components/StyledError'
import type { AssignedBijlage } from '../../bijlagen/types/AssignedBijlage'
import type { Bijlage } from '../../bijlagen/types/Bijlage'
import { useUpdateSubprocesForEntity } from '../hooks/useUpdateSubprocesForEntity'
import type { Subproces } from '../types/Subproces'
import {
  canEditBodem,
  generateBodemDeadline,
  generateBodemStepStatuses,
} from '../util/subproces-bodem-util'
import {
  createSubprocesUpdate,
  getPausedCaptionForStatus,
  getStatusLabelForType,
  isStepDisabled,
} from '../util/subproces-util'

import { SubprocesCard } from './SubprocesCard'

export { SubprocesBodem }

type Props = {
  subproces: Subproces
  subprocessen: Subproces[]
  assignedBijlagen: AssignedBijlage[]
  bijlagen: Bijlage[]
  user: AuthenticatedUser
  isVerantwoordelijke: boolean
  openLogDrawer: (subproces: Subproces) => void

  onClickCancel: () => void
  onClickPause: () => void
  onClickResume: () => void

  onClickCreateBijlage: () => void
  onClickUploadBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickCancelBijlage: (assignedBijlage: AssignedBijlage | Bijlage) => void
  onClickOpleveren: (assignedBijlage: AssignedBijlage) => void
  onClickReviewBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickEditBijlage: (assignedBijlage: AssignedBijlage) => void
  onClickOpenBijlage: (assignedBijlage: Bijlage) => void
}

const SubprocesBodem = ({
  subproces,
  subprocessen,
  assignedBijlagen,
  bijlagen,
  user,
  isVerantwoordelijke,
  openLogDrawer,

  onClickCancel,
  onClickPause,
  onClickResume,

  onClickCreateBijlage,
  onClickUploadBijlage,
  onClickCancelBijlage,
  onClickOpleveren,
  onClickReviewBijlage,
  onClickEditBijlage,
  onClickOpenBijlage,
}: Props) => {
  const {
    mutate: updateSubprocesForEntity,
    error,
    isPending,
  } = useUpdateSubprocesForEntity()

  const { statuses } = generateBodemStepStatuses({ subproces })
  const canEdit = canEditBodem({ user, subproces })

  const getStepProps = (status: keyof typeof statuses) => ({
    label: getStatusLabelForType(subproces.type, status),
    status: statuses[status],
    caption: getPausedCaptionForStatus({ subproces, status }),
    disabled: isStepDisabled({ subproces, expectedStatus: status, isPending }),
  })

  const onSubmit = () => {
    const body = createSubprocesUpdate({
      subproces,
      status: 'review',
      dueDate: generateBodemDeadline('review'),
    })

    updateSubprocesForEntity(body)
  }

  const onReview = () => {
    const body = createSubprocesUpdate({
      subproces,
      status: 'done',
    })

    updateSubprocesForEntity(body)
  }

  const onAanvullendOnderzoekNodig = () => {
    const body = createSubprocesUpdate({
      subproces,
      status: 'aanvullend_onderzoek_nodig',
      dueDate: generateBodemDeadline(
        'aanvullend_onderzoek_nodig',
        subproces.dueDate,
      ),
    })

    updateSubprocesForEntity(body)
  }

  const onComplete = () => {
    const body = createSubprocesUpdate({ subproces, status: 'done' })

    updateSubprocesForEntity(body)
  }

  const samenstellenAanvraag = getStepProps('samenstellen_aanvraag')
  const review = getStepProps('review')
  const aanvullendOnderzoek = getStepProps('aanvullend_onderzoek_nodig')
  const done = getStepProps('done')

  const steps: Step[] = [
    {
      label: samenstellenAanvraag.label,
      status: samenstellenAanvraag.status,
      caption: samenstellenAanvraag.caption,
      content: (
        <>
          <Typography>
            Stel het Combi schetsontwerp samen. Zodra dit wordt opgeleverd wordt
            het Bodemadvies uitgevraagd.
          </Typography>

          {subproces.status === 'samenstellen_aanvraag' &&
            subproces.dueDate && (
              <PropertyRow
                label="Deadline voor opleveren bodemadvies"
                value={formatDate(generateBodemDeadline('review'))}
              />
            )}

          {canEdit && (
            <ConfirmButton
              disabled={samenstellenAanvraag.disabled}
              variant="contained"
              startIcon={
                <PendingIcon isPending={isPending} icon={<ApprovalIcon />} />
              }
              onConfirm={onSubmit}
            >
              Bodemadvies uitvragen
            </ConfirmButton>
          )}
        </>
      ),
    },
    {
      label: review.label,
      status: review.status,
      caption: review.caption,
      content: (
        <>
          <Typography>
            Stel het bodemadvies op en lever dit op in CROW 400 bodem. Als er
            eerst aanvullend onderzoek nodig is geef dat aan.
          </Typography>

          <Typography>
            Ontbreekt er informatie of is er eerst een sanering nodig, pauzeer
            dan het proces met de juiste reden.
          </Typography>

          <PropertyRow
            label="Deadline voor aanvullend onderzoek"
            value={formatDate(
              generateBodemDeadline(
                'aanvullend_onderzoek_nodig',
                subproces.dueDate,
              ),
            )}
          />

          {canEdit && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              <ConfirmButton
                variant="outlined"
                disabled={review.disabled}
                onConfirm={onAanvullendOnderzoekNodig}
                startIcon={<PendingIcon isPending={isPending} icon={<></>} />}
              >
                Aanvullend onderzoek nodig
              </ConfirmButton>
              <ConfirmButton
                variant="contained"
                disabled={review.disabled}
                onConfirm={onReview}
                startIcon={
                  <PendingIcon isPending={isPending} icon={<GradingIcon />} />
                }
              >
                Bodemadvies opleveren
              </ConfirmButton>
            </Box>
          )}
        </>
      ),
    },
    {
      label: aanvullendOnderzoek.label,
      caption: aanvullendOnderzoek.caption,
      status: aanvullendOnderzoek.status,
      content: (
        <>
          <Typography>
            Er is aangegeven dat er aanvullend bodemonderzoek nodig is. Als dit
            is afgerond, lever dan het bodemadvies op in CROW 400 bodem.
          </Typography>

          {canEdit && (
            <ConfirmButton
              variant="contained"
              disabled={aanvullendOnderzoek.disabled}
              onConfirm={onComplete}
              startIcon={
                <PendingIcon
                  isPending={isPending}
                  icon={<PlaylistPlayIcon />}
                />
              }
            >
              Bodemadvies opleveren
            </ConfirmButton>
          )}
        </>
      ),
    },
    {
      label: done.label,
      status: done.status,
      content: (
        <>
          {subproces.status === 'done' && subproces.completed && (
            <Typography>{`${getStatusLabelForType(
              subproces.type,
              subproces.status,
            )} op ${formatDate(subproces.completed.date)} door ${
              subproces.completed.organisatie
            }`}</Typography>
          )}
        </>
      ),
    },
  ]

  return (
    <SubprocesCard
      subproces={subproces}
      subprocessen={subprocessen}
      user={user}
      openLogDrawer={openLogDrawer}
      onClickCancel={onClickCancel}
      onClickPause={onClickPause}
      onClickResume={onClickResume}
      assignedBijlagen={assignedBijlagen}
      bijlagen={bijlagen}
      isVerantwoordelijke={isVerantwoordelijke}
      onClickCreateBijlage={onClickCreateBijlage}
      onClickUploadBijlage={onClickUploadBijlage}
      onClickCancelBijlage={onClickCancelBijlage}
      onClickOpleveren={onClickOpleveren}
      onClickReviewBijlage={onClickReviewBijlage}
      onClickEditBijlage={onClickEditBijlage}
      onClickOpenBijlage={onClickOpenBijlage}
    >
      <Stepper steps={steps} />

      <StyledError error={error} sx={{ marginBottom: 0, marginTop: 2 }} />
    </SubprocesCard>
  )
}
